import Box, { type BoxProps } from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LayoutLeftIcon, LogoIcon, Menu01Icon } from '@shared/ui/icons';
import { AppBarStyled } from './app-bar.styled';
import { DrawerStyled } from './drawer.styled';
import { DrawerHeaderStyled } from './drawer-header.styled';
import { NavigationItemsList } from './nav-items-list';
import { UserMenu } from './user-menu';
import { useLayout, useNavigationItems } from '../model';

type LayoutProps = {
  children: React.ReactNode;
  topElement?: React.ReactNode;
  containerProps?: BoxProps;
};

export function Layout({ children, topElement, containerProps }: LayoutProps) {
  const layout = useLayout();
  const navigation = useNavigationItems();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        height: '100vh',
        background:
          theme.palette.mode === 'light'
            ? theme.palette.black[950]
            : theme.palette.grayTrue[900],
      })}
    >
      <AppBarStyled position="fixed" open={layout.isDrawerOpen} elevation={0}>
        <Toolbar sx={{ px: 3 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={layout.toggleDrawer}
            sx={[!isMobile && layout.isDrawerOpen && { display: 'none' }]}
          >
            {isMobile ? (
              <Menu01Icon sx={{ color: '#888888' }} />
            ) : (
              <LayoutLeftIcon sx={{ color: '#888888' }} />
            )}
          </IconButton>
          <Box ml="auto">
            <UserMenu />
          </Box>
        </Toolbar>
      </AppBarStyled>
      <DrawerStyled
        elevation={0}
        variant={isMobile ? 'temporary' : 'permanent'}
        open={layout.isDrawerOpen}
        onClose={isMobile ? layout.closeDrawer : undefined}
        sx={isMobile ? { zIndex: (theme) => theme.zIndex.drawer } : undefined}
        slotProps={
          isMobile
            ? { paper: { sx: { top: '64px', height: 'calc(100% - 64px)' } } }
            : undefined
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <DrawerHeaderStyled
            sx={{
              justifyContent: layout.isDrawerOpen ? 'space-between' : 'center',
              display: isMobile ? 'none' : 'flex',
            }}
          >
            <LogoIcon
              sx={[
                {
                  color: '#84D300',
                  height: 21,
                  width: 85,
                  transition: 'opacity 0.5s ease-in-out, clip-path 0.5s ease-in-out',
                },
                layout.isDrawerOpen
                  ? { opacity: 1, clipPath: 'inset(0 0% 0 0)' }
                  : { opacity: 0, clipPath: 'inset(0 100% 0 0)' },
              ]}
            />
            <IconButton onClick={layout.closeDrawer} size="medium">
              <LayoutLeftIcon sx={{ color: '#888888' }} />
            </IconButton>
          </DrawerHeaderStyled>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
                height: 0,
                width: 0,
              },
            }}
          >
            <NavigationItemsList navItems={navigation.items} />
          </Box>
          <NavigationItemsList navItems={navigation.settings} />
        </Box>
      </DrawerStyled>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DrawerHeaderStyled />
        <Box
          bgcolor="background.default"
          display="flex"
          flexDirection="column"
          sx={{
            borderTopLeftRadius: isMobile ? 0 : 12,
            overflow: 'auto',
            flex: 1,
          }}
        >
          {topElement}
          <Box p={isMobile ? 1 : 2} flex={1} {...containerProps}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
